import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomerNamesToStringPipe, CustomerNamesToStringService } from 'projects/shared/lib/pipes/customer/customernames-to-string/customernames-to-string.pipe';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { IConfirmDialogService } from 'shared/services/ui/dialogs/confirm/confirm-dialog-service.abstract';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { CustomerDto, EError } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { firstValueFrom } from 'rxjs';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-user-login-customer-select-dialog',
  imports: [
    CommonModule,
    FormsModule,
    CustomerNamesToStringPipe
  ],
  templateUrl: './user-login-customer-select-dialog.component.html',
  styleUrls: ['./user-login-customer-select-dialog.component.scss']
})
export class UserLoginCustomerSelectDialogComponent implements OnInit {

  @Output() modalClose: EventEmitter<void> = new EventEmitter();

  private logger: LoggerService;
  public customerList: CustomerDto[] | undefined;
  public customerSelected: CustomerDto | undefined;
  private readonly authService: IAuthService = inject(IAuthServiceToken);
  protected readonly userService: IUserService = inject(IUserServiceToken);
  private readonly toastService: IToastService = inject(IToastServiceToken);

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);

    let isMultiCustomerUser = this.authService.isMultiCustomerUser();
    if (isMultiCustomerUser == false) {
      throw new Error("Missing permission for selecting a customer in user login select dialog.");
    }
  }

  public ngOnInit(): void {
    this.customerListLoadAsync().then(
      () => {
        if (this.customerList == undefined || this.customerList.length <= 0) {
          this.userService.userLogout();
          return;
        }

        if (this.customerList.length == 1) {
          this.customerSelectAsync(this.customerList[0]);
          return;
        } else {
          this.customerSelected = this.customerList[0];
        }
      }
    );
  }

  protected async customerListLoadAsync() {
    try {
      let result = await firstValueFrom(this.userService.userCustomerListGet());
      this.customerList = result.customerList;
    } catch (error) {
      this.logger.error(error, this.constructor.name);
    }
  }

  public async cancelAsync() {
    // request confirmation
    ServiceLocator.injector.get(IConfirmDialogService).confirmDialogShow(
      () => {
        // cancel -> nothing / hide confirmation
      },
      $localize`:@@UserLoginCustomerSelectDialogCancelConfirmationDialogTitle:Cancel customer selection?`,
      $localize`:@@UserLoginCustomerSelectDialogCancelConfirmationDialogMessage:Canceling this customer selection results in a logout.`,
      () => {
        // yes -> logout
        this.userService.userLogout();
        this.modalClose.emit();
      },
      () => {
        // no -> nothing / hide confirmation
      }
    );
  }

  public async customerSelectAsync(customerUser: CustomerDto | undefined) {
    if (customerUser == undefined) {
      return;
    }

    try {
      let accessJWT = await firstValueFrom(this.userService.userSwitchCustomer(customerUser.customerNumber));
      if (accessJWT.length <= 0) {
        let message = $localize`:@@UserLoginCustomerSelectDialogFailedNoAccessJWTReceived:No new credentials received`;
        this.customerSelectFailedToastShow(message);
        return;
      }
      let accessJWTStored = this.authService.accessJWTSet(accessJWT);
      if (accessJWTStored != undefined) {
        let message = $localize`:@@UserLoginCustomerSelectDialogSuccessMessage:You're now shopping for:`;
        message += `<br><br>`;
        message += CustomerNamesToStringService.transform(customerUser);
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Success,
          title: $localize`:@@UserLoginCustomerSelectDialogSuccessTitle:Customer selected`,
          message: message,
        }));
        setTimeout(() => {
          firstValueFrom(this.userService.userFavoritesInitialize());
        });

        this.modalClose.emit();
      } else {
        let message = $localize`:@@UserLoginCustomerSelectDialogFailedAccessJwtNotReplaced:Permissions could not be updated.`;
        this.customerSelectFailedToastShow(message);
      }

    } catch (error) {
      let message = $localize`:@@UserLoginCustomerSelectDialogFailed:Customer selection failed.`;
      this.customerSelectFailedToastShow(message);
      this.logger.error(error, this.constructor.name);
    }
  }

  private customerSelectFailedToastShow(message?: string | undefined, type: EToastItemType | undefined = EToastItemType.Danger) {
    this.toastService.toastItemAdd(new ToastItem({
      type: type,
      title: $localize`:@@UserLoginCustomerSelectDialogFailedTitle:Customer selection failed`,
      message: message,
      code: ErrorCodeService.formatErrorCode(EError.F200021)
    }));
  }

}
